import React, { createContext, forwardRef } from 'react';

import { LinkProps } from '@mui/material/Link';
import {
  createTheme,
} from '@mui/material/styles';
import {
  Link as RouterLink,
  LinkProps as RouterLinkProps,
} from 'react-router-dom';

import '@fontsource/nunito';
import '@fontsource/poppins';
import '@fontsource/nunito/200.css';
import '@fontsource/poppins/200.css';
import '@fontsource/nunito/300.css';
import '@fontsource/poppins/300.css';
import '@fontsource/nunito/400.css';
import '@fontsource/poppins/400.css';
import '@fontsource/nunito/500.css';
import '@fontsource/poppins/500.css';
import '@fontsource/nunito/600.css';
import '@fontsource/poppins/600.css';
import '@fontsource/nunito/700.css';
import '@fontsource/poppins/700.css';
import '@fontsource/nunito/800.css';
import '@fontsource/poppins/800.css';
import '@fontsource/nunito/900.css';
import '@fontsource/poppins/900.css';

const LinkBehavior = forwardRef<any, Omit<RouterLinkProps, 'to'> & { href: RouterLinkProps['to'] }>(
  (props, ref) => {
    const { href, ...other } = props;
    // Map href (MUI) -> to (react-router)
    return <RouterLink ref={ref} to={href} {...other} />;
  });

export const ThemeContext = createContext({
  initDarkMode: () => {},
  initLightMode: () => {},
});

export type SubjectColorKeys = 'chemistry' | 'physics' | 'mathsAdv' | 'mathsExt1' | 'mathsExt2';

export type BaseColorKeys = 'primary' | 'darkPrimary' | 'secondary' | 'highlight' | 'gray';

export type PureColorKeys = 'turquoise' | 'lightTurquoise' | 'darkTurquoise' | 'red' | 'lightRed' | 'darkRed' | 'orange' | 'lightOrange' | 'darkOrange' | 'yellow' | 'lightYellow' | 'darkYellow' | 'green' | 'lightGreen' | 'darkGreen' | 'blue' | 'lightBlue' | 'darkBlue'

export type BackgroundColorKeys = 'backdrop' | 'background';

export type NotifierColorKeys = 'error' | 'warning' | 'info' | 'success';

export type ColorKeys = BaseColorKeys | PureColorKeys | SubjectColorKeys | BackgroundColorKeys | NotifierColorKeys | 'mistake' | 'divider';

export enum ThemeMode {
  Light = 'light',
  Dark = 'dark',
}

const lightTheme: any = {
  palette: {
    mode: ThemeMode.Light,
    primary: {
      main: '#73DDCD',
      dark: '#4DA7A1',
      light: '#A9EAE1',
      contrastText: '#fff',
    },
    darkPrimary: {
      main: '#267175',
      dark: '#003B49',
      light: '#4DA7A1',
      contrastText: '#fff',
    },
    secondary: {
      main: '#65BFF0',
      light: '#A6DBF8',
      dark: '#4E96BD',
      contrastText: '#fff',
    },
    highlight: {
      main: '#FF5F5F',
      light: '#FFA3A3',
      dark: '#C74C4C',
      contrastText: '#fff',
    },
    chemistry: {
      main: '#65BFF0',
      light: '#A6DBF8',
      dark: '#4E96BD',
      contrastText: '#fff',
    },
    physics: {
      main: '#79D98C',
      light: '#ADEAB9',
      dark: '#61AC74',
      contrastText: '#fff',
    },
    mathsAdv: {
      main: '#FFD166',
      light: '#FFE4A6',
      dark: '#D2A551',
      contrastText: '#fff',
    },
    mathsExt1: {
      main: '#FF9C52',
      light: '#FFC69C',
      dark: '#CE7A40',
      contrastText: '#fff',
    },
    mathsExt2: {
      main: '#FF5F5F',
      light: '#FFA3A3',
      dark: '#C74C4C',
      contrastText: '#fff',
    },
    backdrop: {
      main: '#F4F7FA',
      dark: '#e0e6eb',
      light: '#FFFFFF',
      contrastText: 'rgba(0,0,0,0.87)',
    },
    gray: {
      main: '#788791',
      dark: '#374043',
      light: '#9AA5AC',
      contrastText: '#fff',
    },
    error: {
      main: '#FF5F5F',
      light: '#FFA3A3',
      dark: '#C74C4C',
      contrastText: '#fff',
    },
    warning: {
      main: '#FF9C52',
      light: '#FFC69C',
      dark: '#CE7A40',
      contrastText: '#fff',
    },
    info: {
      main: '#65BFF0',
      light: '#A6DBF8',
      dark: '#4E96BD',
      contrastText: '#fff',
    },
    success: {
      main: '#61AC74',
      light: '#ADEAB9',
      dark: '#497F5B',
      contrastText: '#fff',
    },
    mistake: {
      main: '#FFA3A3',
      light: '#FFE6E6',
      dark: '#FF5F5F',
      contrastText: 'rgba(0,0,0,0.87)',
    },
    turquoise: {
      main: '#73DDCD',
      dark: '#4DA7A1',
      light: '#A9EAE1',
      contrastText: '#fff',
    },
    lightTurquoise: {
      main: '#A9EAE1',
      dark: '#73DDCD',
      light: '#DEF7F4',
      contrastText: 'rgba(0,0,0,0.87)',
    },
    darkTurquoise: {
      main: '#4DA7A1',
      dark: '#267175',
      light: '#73DDCD',
      contrastText: '#fff',
    },
    red: {
      main: '#FF5F5F',
      dark: '#C74C4C',
      light: '#FFA3A3',
      contrastText: '#fff',
    },
    lightRed: {
      main: '#FFA3A3',
      dark: '#FF5F5F',
      light: '#FFE6E6',
      contrastText: 'rgba(0,0,0,0.87)',
    },
    darkRed: {
      main: '#C74C4C',
      dark: '#8F3838',
      light: '#FF5F5F',
    },
    orange: {
      main: '#FF9C52',
      dark: '#CE7A40',
      light: '#FFC69C',
      contrastText: '#fff',
    },
    lightOrange: {
      main: '#FFC69C',
      dark: '#FF9C52',
      light: '#FFF0E6',
      contrastText: 'rgba(0,0,0,0.87)',
    },
    darkOrange: {
      main: '#CE7A40',
      dark: '#9C582F',
      light: '#FF9C52',
      contrastText: '#fff',
    },
    yellow: {
      main: '#FFD166',
      dark: '#D2A551',
      light: '#FFE4A6',
      contrastText: '#fff',
    },
    lightYellow: {
      main: '#FFE4A6',
      dark: '#FFD166',
      light: '#FFF7E6',
      contrastText: 'rgba(0,0,0,0.87)',
    },
    darkYellow: {
      main: '#D2A551',
      dark: '#A57A3B',
      light: '#FFD166',
      contrastText: '#fff',
    },
    green: {
      main: '#79D98C',
      dark: '#61AC74',
      light: '#ADEAB9',
      contrastText: '#fff',
    },
    lightGreen: {
      main: '#ADEAB9',
      dark: '#79D98C',
      light: '#E1FAE5',
      contrastText: 'rgba(0,0,0,0.87)',
    },
    darkGreen: {
      main: '#61AC74',
      dark: '#497F5B',
      light: '#79D98C',
      contrastText: '#fff',
    },
    blue: {
      main: '#65BFF0',
      dark: '#4E96BD',
      light: '#A6DBF8',
      contrastText: '#fff',
    },
    lightBlue: {
      main: '#A6DBF8',
      dark: '#65BFF0',
      light: '#E6F6FF',
      contrastText: 'rgba(0,0,0,0.87)',
    },
    darkBlue: {
      main: '#4E96BD',
      dark: '#366D8A',
      light: '#65BFF0',
      contrastText: '#fff',
    },
  },
};

const darkTheme: any = {
  palette: {
    mode: ThemeMode.Dark,
    primary: {
      main: '#A9EAE1',
      dark: '#DEF7F4',
      light: '#73DDCD',
      contrastText: 'rgba(0,0,0,0.87)',
    },
    darkPrimary: {
      main: '#FFFFFF',
      dark: '#D3D3D3',
      light: '#D3D3D3',
      contrastText: '#003B49',
    },
    secondary: {
      main: '#65BFF0',
      light: '#4E96BD',
      dark: '#A6DBF8',
      contrastText: '#fff',
    },
    highlight: {
      main: '#FF5F5F',
      light: '#C74C4C',
      dark: '#FFA3A3',
      contrastText: '#fff',
    },
    chemistry: {
      main: '#65BFF0',
      light: '#4E96BD',
      dark: '#A6DBF8',
      contrastText: '#fff',
    },
    physics: {
      main: '#79D98C',
      light: '#61AC74',
      dark: '#ADEAB9',
      contrastText: '#fff',
    },
    mathsAdv: {
      main: '#FFD166',
      light: '#D2A551',
      dark: '#FFE4A6',
      contrastText: '#fff',
    },
    mathsExt1: {
      main: '#FF9C52',
      light: '#CE7A40',
      dark: '#FFC69C',
      contrastText: '#fff',
    },
    mathsExt2: {
      main: '#FF5F5F',
      light: '#C74C4C',
      dark: '#FFA3A3',
      contrastText: '#fff',
    },
    backdrop: {
      main: '#1a1a1a',
      dark: '#404040',
      light: '#000000',
      contrastText: '#fff',
    },
    gray: {
      main: '#aaaaaa',
      dark: '#fff',
      light: '#525252',
      contrastText: '#fff',
    },
    error: {
      main: '#FF5F5F',
      light: '#C74C4C',
      dark: '#FFA3A3',
      contrastText: '#fff',
    },
    warning: {
      main: '#FF9C52',
      light: '#CE7A40',
      dark: '#FFC69C',
      contrastText: '#fff',
    },
    info: {
      main: '#65BFF0',
      light: '#4E96BD',
      dark: '#A6DBF8',
      contrastText: '#fff',
    },
    success: {
      main: '#61AC74',
      light: '#497F5B',
      dark: '#ADEAB9',
      contrastText: '#fff',
    },
    mistake: {
      main: '#FFA3A3',
      light: '#FFE6E6',
      dark: '#FF5F5F',
      contrastText: 'rgba(0,0,0,0.87)',
    },
    turquoise: {
      main: '#73DDCD',
      dark: '#A9EAE1',
      light: '#4DA7A1',
      contrastText: '#fff',
    },
    lightTurquoise: {
      main: '#A9EAE1',
      dark: '#DEF7F4',
      light: '#73DDCD',
      contrastText: 'rgba(0,0,0,0.87)',
    },
    darkTurquoise: {
      main: '#4DA7A1',
      dark: '#73DDCD',
      light: '#267175',
      contrastText: '#fff',
    },
    red: {
      main: '#FF5F5F',
      dark: '#FFA3A3',
      light: '#C74C4C',
      contrastText: '#fff',
    },
    lightRed: {
      main: '#FFA3A3',
      dark: '#FFE6E6',
      light: '#FF5F5F',
      contrastText: 'rgba(0,0,0,0.87)',
    },
    darkRed: {
      main: '#C74C4C',
      dark: '#FF5F5F',
      light: '#8F3838',
    },
    orange: {
      main: '#FF9C52',
      dark: '#FFC69C',
      light: '#CE7A40',
      contrastText: '#fff',
    },
    lightOrange: {
      main: '#FFC69C',
      dark: '#FFF0E6',
      light: '#FF9C52',
      contrastText: 'rgba(0,0,0,0.87)',
    },
    darkOrange: {
      main: '#CE7A40',
      dark: '#FF9C52',
      light: '#9C582F',
      contrastText: '#fff',
    },
    yellow: {
      main: '#FFD166',
      dark: '#FFE4A6',
      light: '#D2A551',
      contrastText: '#fff',
    },
    lightYellow: {
      main: '#FFE4A6',
      dark: '#FFF7E6',
      light: '#FFD166',
      contrastText: 'rgba(0,0,0,0.87)',
    },
    darkYellow: {
      main: '#D2A551',
      dark: '#FFD166',
      light: '#A57A3B',
      contrastText: '#fff',
    },
    green: {
      main: '#79D98C',
      dark: '#ADEAB9',
      light: '#61AC74',
      contrastText: '#fff',
    },
    lightGreen: {
      main: '#ADEAB9',
      dark: '#E1FAE5',
      light: '#79D98C',
      contrastText: 'rgba(0,0,0,0.87)',
    },
    darkGreen: {
      main: '#61AC74',
      dark: '#79D98C',
      light: '#497F5B',
      contrastText: '#fff',
    },
    blue: {
      main: '#65BFF0',
      dark: '#A6DBF8',
      light: '#4E96BD',
      contrastText: '#fff',
    },
    lightBlue: {
      main: '#A6DBF8',
      dark: '#E6F6FF',
      light: '#65BFF0',
      contrastText: 'rgba(0,0,0,0.87)',
    },
    darkBlue: {
      main: '#4E96BD',
      dark: '#65BFF0',
      light: '#366D8A',
      contrastText: '#fff',
    },
  },
};

const baseTheme: any = {
  components: {
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
      } as LinkProps,
    },
    MuiButtonBase: {
      defaultProps: {
        LinkComponent: LinkBehavior,
      },
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 8,
    drawerWidth: 240,
    appbarHeight: 64,
  },
  typography: {
    fontSize: 14,
    fontFamily: '"Nunito", "Helvetica", "Arial", sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    htmlFontSize: 16,
    h1: {
      fontSize: '6rem',
      fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
      fontWeight: 300,
      fontWeightBold: 600,
      lineHeight: 1.167,
      letterSpacing: '-0.01562em',
    },
    h2: {
      fontSize: '3rem',
      fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
      fontWeight: 300,
      fontWeightBold: 600,
      lineHeight: 1.167,
      letterSpacing: '0em',
    },
    h3: {
      fontSize: '2.125rem',
      fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      fontWeightBold: 600,
      lineHeight: 1.235,
      letterSpacing: '0.00735em',
    },
    h4: {
      fontSize: '1.5rem',
      fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      fontWeightBold: 600,
      lineHeight: 1.334,
      letterSpacing: '0em',
    },
    h5: {
      fontSize: '1.25rem',
      fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      fontWeightBold: 600,
      lineHeight: 1.6,
      letterSpacing: '0.0075em',
    },
    h6: {
      fontSize: '1rem',
      fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      fontWeightBold: 600,
      lineHeight: 1.4,
      letterSpacing: '0.0075em',
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.75,
      letterSpacing: '0.00938em',
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 700,
      lineHeight: 1.57,
      letterSpacing: '0.00714em',
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0.00938em',
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.43,
      letterSpacing: '0.01071em',
    },
    button: {
      fontSize: '0.875rem',
      fontFamily: '"Poppins", "Helvetica", "Arial", sans-serif',
      fontWeight: 600,
      lineHeight: 1.75,
      letterSpacing: '0.02857em',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.66,
      letterSpacing: '0.03333em',
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 2.66,
      letterSpacing: '0.08333em',
    },
  },
};

export const isDark = (theme: any) => theme?.palette?.mode === ThemeMode.Dark;

export const getThemeObject = (mode: (ThemeMode | undefined)) => (
  { ...baseTheme, ...(mode === ThemeMode.Dark ? darkTheme : lightTheme) }
);

export default (mode: (ThemeMode | undefined)) => createTheme(getThemeObject(mode));
